<template>
  <el-form
    ref="form"
    :model="form"
    :rules="rules"
    label-width="120px"
    label-position="top"
    class="company-modal__inputs mb_16"
  >
    <el-form-item
      :label="$t('adminTitles.Title')"
      prop="name"
      class="company-modal__input company-modal__input_fullwidth"
    >
      <BaseTooltip
        class="company-modal__input-tooltip"
        :tooltip-text="$t('insuranceCompanies.HereYouCanUpdateDetails')"
      />
      <el-input
        v-model="form.name"
        :placeholder="$t('insuranceCompanies.EnterTitle')"
      />
    </el-form-item>

    <el-form-item
      :label="$t('claims.card.Carrier')"
      class="company-modal__input company-modal__input_fullwidth"
    >
      <el-select
        v-model="carrier"
        filterable
        style="width: 100%"
        :placeholder="$t('adminTitles.Select')"
        :class="{ 'company-modal__carrier--icon': selectedCarrierIcon }"
        @change="pickCarrier"
      >
        <template #prefix>
          <span v-if="selectedCarrierIcon" class="company-modal__carrier-prefix">
            <img class="company-modal__carrier-icon" alt="" :src="selectedCarrierIcon"/>
          </span>
        </template>
        <el-option
          v-for="c in carrierOptions"
          :key="c.code"
          :value="c.code"
          :label="c.displayName"
        >
          <span class="company-modal__carrier-option">
            <img class="company-modal__carrier-icon" alt="" :src="carrierIcon(c.code)"/>
            {{ c.displayName }}
          </span>
        </el-option>
        <el-option :value="OTHER" :label="$t('carriers.import.diff.value.OTHER')"/>
      </el-select>
      <p v-if="carrierSuggested" class="company-modal__carrier-hint">
        {{ $t('insuranceCompanies.SuggestedFromName') }}
      </p>
    </el-form-item>

    <el-form-item
      :label="$t('adminTitles.email')"
      prop="mainEmailSave"
      class="company-modal__input"
    >
      <el-input
        v-model="form.mainEmailSave"
        :placeholder="$t('adminTitles.EnterEmail')"
      />
    </el-form-item>

    <el-form-item
      prop="mainPhoneSave"
      class="company-modal__input"
      :label="$t('adminTitles.phone')"
    >
      <input-phone
        :value="form.mainPhoneSave"
        ref="phoneInput"
        :show-label="false"
        @valueChange="form.mainPhoneSave = $event"/>
    </el-form-item>

    <el-form-item
      :label="$t('insuranceCompanies.Logo')"
      class="company-modal__input company-modal__input_fullwidth"
      prop="backgroundImageUrl"
    >
      <BaseDocument
        :file-upload-view="!file"
        :type="'logo'"
        :link="file ? file.url : null"
        :delete-btn="!!file"
        :loading="imgLoading"
        :accept-formats="'.jpeg, .jpg, .png'"
        :accept-formats-text="'JPG, PNG'"
        @fileChange="saveFile"
        @delete="form.logoCdnFileIdSave = null; file = null; form.logoCdnFileIdDelete = true; logoFromCarrier = false;"
      ></BaseDocument>
    </el-form-item>
  </el-form>
</template>

<script>
import BaseTooltip from '@/views/RenovaDashboard/components/ui-kit/BaseTooltip';
import BaseDocument from '@/views/RenovaDashboard/components/ui-kit/Cards/BaseDocument';
import InputPhone from '@/views/RenovaDashboard/components/ui-kit/Inputs/InputPhone';
import {pick} from 'lodash';
import {mapGetters} from 'vuex';
import {carrierIcon, hasCarrierMark, suggestCarrier} from '@/utils/carrierCatalog';

// The select's value for "none of these": el-select shows null as empty.
const OTHER = 'other';

export default {
  name: 'MainInformationForm',
  components: {
    BaseTooltip,
    BaseDocument,
    InputPhone,
  },
  props: {
    editableCompany: Object,
  },
  emits: ['formChange'],
  computed: {
    ...mapGetters(['carrierCatalog']),
    carrierOptions() {
      return this.carrierCatalog
        .slice()
        .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
    },
    selectedCarrierIcon() {
      return this.carrier && this.carrier !== OTHER ? carrierIcon(this.carrier) : null;
    },
    savedCarrierCode() {
      return (this.editableCompany && this.editableCompany.carrierCode) || null;
    },
    /** The carrier still follows the name: nobody picked one, and none was saved. */
    carrierFollowsName() {
      return !this.carrierTouched && !this.savedCarrierCode;
    },
    carrierSuggested() {
      return this.carrierFollowsName && Boolean(this.carrier) && this.carrier !== OTHER;
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.emitFormData();
      },
    },
    'form.name': 'suggestFromName',
    carrierCatalog: 'suggestFromName',
  },
  data() {
    return {
      form: {
        name: null,
        mainEmailSave: null,
        mainPhoneSave: null,
        logoCdnFileIdSave: null,
        logoCdnFileIdDelete: false,
        carrierCodeSave: null,
        carrierCodeDelete: false,
      },
      rules: {
        name: [
          {
            required: true,
            message: this.$t('adminTitles.FieldIsRequired'),
            trigger: 'change',
          },
        ],
        mainEmailSave: [
          {
            required: false,
            message: this.$t('adminTitles.FieldIsRequired'),
            trigger: 'blur',
          },
          {
            pattern: /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,24}$/,
            message: this.$t('telemed.invalidEmail'),
            trigger: 'blur',
          },
        ],
        mainPhoneSave: [
          {
            required: false,
            message: this.$t('adminTitles.PhoneNumberIsRequired'),
            trigger: 'change',
            validator: this.phoneValidate,
          },
        ],
      },
      file: null,
      imgLoading: false,
      OTHER,
      // The select's value: a carrier code, OTHER, or '' while nothing is chosen.
      carrier: '',
      // The user chose in the select; from then on the name suggests nothing.
      carrierTouched: false,
      // The logo on show is the chosen carrier's mark, put there by this form —
      // the only kind of logo a later choice may replace.
      logoFromCarrier: false,
      // Bumped per upload, so an answer that arrives late cannot overwrite a newer logo.
      logoUpload: 0,
    };
  },
  created() {
    if (this.editableCompany) {
      this.setDefaultValues();
    }
    if (this.savedCarrierCode) {
      this.setCarrier(this.savedCarrierCode);
    }
    this.$store.dispatch('fetchCarrierCatalog');
  },
  methods: {
    phoneValidate(rule, value, callback) {
      if (value && this.$refs.phoneInput.validate()) {
        callback(new Error());
      } else {
        callback();
      }
    },
    setDefaultValues() {
      this.form = {
        ...this.form,
        ...pick(this.editableCompany, Object.keys(this.form)),
        mainEmailSave: this.editableCompany.mainEmail,
        mainPhoneSave: this.editableCompany.mainPhone,
      };
      if (this.editableCompany.logoUrl) {
        this.file = {
          url: this.editableCompany.logoUrl,
        };
      }
    },
    carrierIcon,
    /** While the carrier follows the name, the name's carrier — or nothing — is the choice. */
    suggestFromName() {
      if (!this.carrierFollowsName) return;
      const suggestion = suggestCarrier(this.form.name, this.carrierCatalog);
      const code = suggestion ? suggestion.code : '';
      if (code !== this.carrier) this.setCarrier(code);
    },
    pickCarrier(value) {
      this.carrierTouched = true;
      this.setCarrier(value);
    },
    /**
     * What is sent on save, and the logo. A chosen carrier lends its mark to a
     * record that has no logo of its own; "Other" keeps whatever is there,
     * except a mark this form put there for a carrier no longer chosen.
     */
    setCarrier(value) {
      this.carrier = value;
      const code = value && value !== OTHER ? value : null;
      this.form.carrierCodeSave = code;
      this.form.carrierCodeDelete = !code && value === OTHER && Boolean(this.savedCarrierCode);
      if (code && (!this.file || this.logoFromCarrier)) {
        this.useCarrierLogo(code);
      } else if (!code && this.logoFromCarrier) {
        this.logoUpload += 1;
        this.logoFromCarrier = false;
        this.file = null;
        this.form.logoCdnFileIdSave = null;
        // Only there because the record's own logo was removed first.
        this.form.logoCdnFileIdDelete = Boolean(this.editableCompany && this.editableCompany.logoUrl);
        this.imgLoading = false;
      }
    },
    useCarrierLogo(code) {
      // The placeholder stands in on screen; it is not a logo to save on the carrier.
      if (!hasCarrierMark(code)) return;
      this.logoFromCarrier = true;
      fetch(carrierIcon(code))
        .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(`${res.status}`))))
        .then((blob) => {
          // Chosen otherwise meanwhile, or the user gave a logo of their own.
          if (this.carrier !== code || !this.logoFromCarrier) return;
          this.uploadLogo(new File([blob], `${code}.png`, { type: 'image/png' }), true);
        })
        // The logo is optional; a carrier without a mark to hand just stays without one.
        .catch(() => {});
    },
    saveFile(e) {
      this.logoFromCarrier = false;
      this.uploadLogo(e.target.files[0], false);
    },
    uploadLogo(file, fromCarrier) {
      this.logoUpload += 1;
      const upload = this.logoUpload;
      this.file = file;
      this.imgLoading = true;
      this.$store.dispatch('SAVE_FILE_TO_CDN', this.file)
        .then((resp) => {
          if (upload !== this.logoUpload) return;
          this.form.logoCdnFileIdSave = resp.cdnFileId;
          this.form.logoCdnFileIdDelete = null;
          this.file = resp;
          this.imgLoading = false;
        })
        .catch(() => {
          if (upload !== this.logoUpload) return;
          this.imgLoading = false;
          if (fromCarrier) {
            this.file = null;
            this.logoFromCarrier = false;
            return;
          }
          this.$notify.error({
            message: this.$t('adminTitles.fileUploadError'),
            duration: 5000,
          });
        });
    },
    emitFormData() {
      this.$emit('formChange', this.form);
    },
  },
};
</script>
<style lang="scss">
.company-modal__inputs {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.company-modal__input {
  width: calc(50% - 8px);
  position: relative;

  &_fullwidth {
    width: 100%;
  }
}

.company-modal__carrier-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.company-modal__carrier-prefix {
  display: flex;
  align-items: center;
  height: 100%;
  padding-left: 6px;
}

// element-fix.scss sets `.el-input--prefix .el-input__inner` to 25px, which
// equals this rule and loads later, so the name ran under the icon.
.company-modal__carrier--icon .el-input--prefix .el-input__inner {
  padding-left: 38px;
}

.company-modal__carrier-icon {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: contain;
}

.company-modal__carrier-hint {
  font-size: 12px;
  line-height: 16px;
  color: #8f8f9f;
  margin-top: 4px;
}

.company-modal__input-tooltip {
  position: absolute;
  right: 0;
  top: -22px;
}
</style>
